import { useCallback, useEffect, useRef, useState } from "react";
import { type Donation, DonationAlertsSocket } from "@/root";

interface UseDonationAlertsOptions {
  /**
   * Donation Alerts Bearer Token.
   * If provided, the hook will connect automatically.
   */
  token?: string;
  /**
   * Callback for when a new donation is received.
   */
  onDonation?: (donation: Donation) => void;
}

interface UseDonationAlertsReturn {
  /**
   * Current connection status.
   */
  isConnected: boolean;
  /**
   * Manually connect to Donation Alerts.
   * @param token Bearer token
   */
  connect: (token: string) => void;
  /**
   * Manually disconnect from Donation Alerts.
   */
  disconnect: () => void;
}

/**
 * Hook for managing Donation Alerts WebSocket connection.
 */
export const useDonationAlerts = (options?: UseDonationAlertsOptions): UseDonationAlertsReturn => {
  const socketRef = useRef<DonationAlertsSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  const onDonationRef = useRef(options?.onDonation);
  useEffect(() => {
    onDonationRef.current = options?.onDonation;
  }, [options?.onDonation]);

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
      socketRef.current = null;
      setIsConnected(false);
    }
  }, []);

  const connect = useCallback(
    (token: string) => {
      // Disconnect existing socket if any
      disconnect();

      if (!token) {
        console.error("Donation Alerts: No token provided");
        return;
      }

      const socket = new DonationAlertsSocket(token);

      socket.onDonation((donation) => {
        // Dispatch custom event to window for backward compatibility/global use
        const event = new CustomEvent<Donation>("onDonation", {
          detail: donation,
        });
        window.dispatchEvent(event);

        // Call optional callback
        onDonationRef.current?.(donation);
      });

      socket.onConnect(() => setIsConnected(true));
      socket.onError(() => setIsConnected(false));
      socket.onDisconnect(() => setIsConnected(false));

      socketRef.current = socket;
    },
    [disconnect],
  );

  // Automatic connection/disconnection based on token
  useEffect(() => {
    if (options?.token) {
      connect(options.token);
    } else {
      // If token is removed, we probably want to disconnect
      disconnect();
    }

    return () => {
      disconnect();
    };
  }, [options?.token, connect, disconnect]);

  return {
    isConnected,
    connect,
    disconnect,
  };
};
